// Stateful incremental viewer compile, for the browser. A full (re)seed on the
// first call and on any structural change — file add/remove, manifest edit,
// dependency/stdlib change, or a file's import surface changing — re-runs
// DISCOVERY (roots, deps, spaces) and reseeds the IncrementalPackageCompiler;
// a content edit to an existing `.arch` file re-resolves only the changed
// declarations. The cold `loadViewer` / `buildViewerState` no longer run here —
// every result, full or incremental, comes from the engine. (Cold survives only
// as the equivalence-test oracle.)
//
// `update(opts)` is a drop-in for `loadViewer(opts)` that keeps state between
// calls — feed it a fresh VFS each edit and it diffs internally.

import { IncrementalPackageCompiler, type Diagnostic } from "@archlang/engine";
import { parseCached } from "@archlang/engine";
import { discoverPackageFromVfs, type DiscoveredPackage } from "./load-package.js";
import { withStubs, type ViewerState } from "./build-state.js";
import { STDLIB_FILES } from "./stdlib-data.js";
import { vfsJoin, type Vfs } from "./vfs.js";

/** Normalise every VFS key to an absolute path (`main.arch` → `/main.arch`),
 *  matching how discovery keys its files. A bare-keyed store would otherwise
 *  diff against the absolute keys the engine holds and never match — forcing a
 *  full reseed each edit (and, before discovery was hardened, dropping the file
 *  entirely). Idempotent for an already-absolute store. */
function toAbsoluteVfs(vfs: Vfs): Map<string, string> {
  const out = new Map<string, string>();
  for (const [k, v] of vfs) out.set(vfsJoin("/", k), v);
  return out;
}

/** A compile result plus whether the symbol/import surface changed this update.
 *  `structural: true` on a full (re)seed or any edit that added/removed/renamed
 *  a declaration — consumers invalidate identity caches accordingly. */
export type IncrementalResult = ViewerState & { readonly structural: boolean };

export interface IncrementalUpdateOptions {
  readonly sources: Vfs;
  readonly rootPath?: string;
  readonly stdlib?: Vfs;
  readonly stdlibPath?: string;
}

/** Seed an incremental compiler from a discovery result (input + resolved deps
 *  + raw file sources). Shared by `IncrementalViewer` and the engine-backed
 *  `loadViewer`. */
export function seedFromDiscovered(ipc: IncrementalPackageCompiler, d: DiscoveredPackage): void {
  ipc.load({
    name: d.input.name,
    manifest: d.input.manifest,
    deps: d.deps,
    spaces: d.input.spaces,
    spaceUses: d.input.spaceUses,
    rootPath: d.input.rootPath,
    files: [...d.sources].map(([uri, content]) => ({ uri, content })),
    documents: d.input.documents,
  });
}

/** Fold the discovery diagnostics (manifest/dependency/stdlib/cycle — which the
 *  compiler doesn't see) in front of the engine's resolve/validate diagnostics,
 *  matching the cold `buildViewerState` ordering (discovery first). */
export function withDiscoveryDiagnostics(
  state: ViewerState,
  discoveryDiags: readonly Diagnostic[],
): ViewerState {
  if (discoveryDiags.length === 0) return state;
  return { ...state, diagnostics: [...discoveryDiags, ...state.diagnostics] };
}

export class IncrementalViewer {
  private readonly ipc = new IncrementalPackageCompiler(parseCached);
  private discovered: DiscoveredPackage | null = null;
  private discoveryDiags: readonly Diagnostic[] = [];
  private prevUserVfs = new Map<string, string>();
  /** The `.arch` file keys the engine holds (user package, under rootPath). */
  private userFiles = new Set<string>();
  /** The rootPath the engine is currently scoped to. A change (e.g. the active
   *  package switching when the open file moves to another package) forces a full
   *  re-discovery — the same VFS resolves to a different package. */
  private prevRootPath: string | null = null;

  /** Serializes `update()` — the engine is stateful, so overlapping async calls
   *  (rapid edits) must not interleave their diff/seed/setFile steps. */
  private chain: Promise<unknown> = Promise.resolve();

  /** Discard all incremental state so the next `update()` performs a full cold
   *  reseed — equivalent to constructing a fresh `IncrementalViewer`. Use this
   *  when the user triggers an explicit "recompute from scratch" action. */
  coldReset(): void {
    this.discovered = null;
    this.discoveryDiags = [];
    this.prevUserVfs = new Map();
    this.userFiles = new Set();
    this.prevRootPath = null;
    this.chain = Promise.resolve();
  }

  /** Compile the workspace, reusing prior work where possible. Returns the same
   *  `ViewerState` shape as `loadViewer`. Calls are serialized. */
  update(opts: IncrementalUpdateOptions): Promise<IncrementalResult> {
    const run = this.chain.then(() => this.doUpdate(opts));
    this.chain = run.catch(() => undefined);
    return run;
  }

  private async doUpdate(opts: IncrementalUpdateOptions): Promise<IncrementalResult> {
    // Normalise keys up front so discovery, the engine's file store, and this
    // viewer's own diff maps (prevUserVfs/userFiles) all speak absolute paths.
    const userVfs = toAbsoluteVfs(opts.sources);
    const rootPath = opts.rootPath ?? "/";

    if (this.needsFullLoad(userVfs, rootPath)) {
      const discovered = await discoverPackageFromVfs({
        userVfs,
        userRoot: rootPath,
        stdlibVfs: opts.stdlib ?? STDLIB_FILES,
        stdlibRoot: opts.stdlibPath ?? "/__stdlib__",
      });
      seedFromDiscovered(this.ipc, discovered);
      this.discovered = discovered;
      this.discoveryDiags = discovered.diagnostics;
      this.userFiles = new Set(discovered.sources.keys());
      this.prevUserVfs = new Map(userVfs);
      this.prevRootPath = rootPath;
      return { ...withStubs(withDiscoveryDiagnostics(this.ipc.state(), this.discoveryDiags)), structural: true };
    }

    // Incremental: only the changed `.arch` files re-resolve.
    let structural = false;
    for (const [key, content] of userVfs) {
      if (this.userFiles.has(key) && content !== this.prevUserVfs.get(key)) {
        if (this.ipc.setFile(key, content)) structural = true;
      }
    }
    this.prevUserVfs = new Map(userVfs);
    return { ...withStubs(withDiscoveryDiagnostics(this.ipc.state(), this.discoveryDiags)), structural };
  }

  /** True when the edit can't be applied incrementally and a full reseed is
   *  needed: first call, a file added/removed, a non-`.arch`/manifest change, or
   *  a `.arch` file whose `use` set changed (a new dependency may need loading). */
  private needsFullLoad(userVfs: Vfs, rootPath: string): boolean {
    if (!this.discovered) return true;
    if (rootPath !== this.prevRootPath) return true; // active package switched
    if (userVfs.size !== this.prevUserVfs.size) return true;
    for (const key of userVfs.keys()) if (!this.prevUserVfs.has(key)) return true;

    for (const [key, content] of userVfs) {
      if (content === this.prevUserVfs.get(key)) continue; // unchanged
      if (!this.userFiles.has(key)) return true; // manifest / non-arch / unknown file
      const prevUses = JSON.stringify(this.discovered.files.get(key)?.uses ?? []);
      const nextUses = JSON.stringify(parseCached(content, key).file.uses);
      if (prevUses !== nextUses) return true; // import surface changed
    }
    return false;
  }
}
